import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";

export type DSLinkButtonVariant = "default" | "danger";

// Base props shared between button and anchor
interface DSLinkButtonBaseProps {
  variant?: DSLinkButtonVariant;
  /** Optional leading icon (FontAwesome IconProp) */
  icon?: IconProp;
  children: React.ReactNode;
  dataTestId: string;
}

// Props when used as a button (no href)
interface DSLinkButtonAsButton
  extends
    DSLinkButtonBaseProps,
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  href?: never;
  target?: never;
  rel?: never;
  ref?: React.Ref<HTMLButtonElement>;
}

// Props when used as an anchor (with href)
interface DSLinkButtonAsAnchor
  extends
    DSLinkButtonBaseProps,
    Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "children"> {
  href: string;
  ref?: React.Ref<HTMLAnchorElement>;
}

export type DSLinkButtonProps =
  | DSLinkButtonAsButton
  | DSLinkButtonAsAnchor;

/**
 * DSLinkButton - A text-based button styled as a link for inline use
 *
 * Can be used as either:
 * - A button (default): For onClick actions
 * - An anchor: When href is provided, renders as an <a> tag
 */
export const DSLinkButton = ({
  variant = "default",
  icon,
  children,
  dataTestId,
  ref,
  ...props
}: DSLinkButtonProps) => {
  const baseStyles =
    "inline-flex items-center font-semibold gap-xs bg-transparent border-none transition-colors underline-offset-2 decoration-dotted";

  const disabledStyles = "text-placeholder cursor-not-allowed";

  const variantStyles = {
    default:
      "text-brand hover:text-indigo-600 dark:hover:text-indigo-300 hover:underline cursor-pointer",
    danger:
      "text-danger hover:text-red-600 dark:hover:text-red-300 hover:underline cursor-pointer",
  };

  const isAnchor = "href" in props && props.href !== undefined;
  const disabled = "disabled" in props ? props.disabled : false;

  const className = `${baseStyles} ${
    disabled ? disabledStyles : variantStyles[variant]
  }`;

  const content = (
    <>
      {icon && <FontAwesomeIcon icon={icon} />}
      {children}
    </>
  );

  if (isAnchor) {
    const { href, ...anchorProps } = props as DSLinkButtonAsAnchor;
    return (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={href}
        data-testid={dataTestId}
        data-intercom-target={dataTestId}
        className={className}
        {...anchorProps}
      >
        {content}
      </a>
    );
  }

  const buttonProps = props as DSLinkButtonAsButton;
  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      type="button"
      disabled={disabled}
      data-testid={dataTestId}
      data-intercom-target={dataTestId}
      className={className}
      {...buttonProps}
    >
      {content}
    </button>
  );
};

DSLinkButton.displayName = "DSLinkButton";
